import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TreePine, Lock, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event which fires when
    // the user arrives via a recovery link
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setSessionReady(true);
      }
    });

    // Also check if we already have a session (user may have already been verified)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({ title: "Erreur", description: "Les mots de passe ne correspondent pas.", variant: "destructive" });
      return;
    }

    if (password.length < 6) {
      toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 6 caractères.", variant: "destructive" });
      return;
    }

    setSubmitting(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setDone(true);
      toast({ title: "Mot de passe mis à jour", description: "Vous pouvez maintenant vous connecter." });
      setTimeout(() => navigate("/auth"), 3000);
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-forest flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TreePine className="h-8 w-8 text-primary-foreground" />
            <span className="font-display text-2xl font-bold text-primary-foreground">
              Chimay<span className="text-amber">Bois</span>
            </span>
          </div>
          <p className="text-primary-foreground/70 text-sm">
            Choisissez votre nouveau mot de passe
          </p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-elevated space-y-4">
          {done ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle className="h-12 w-12 text-primary mx-auto" />
              <h2 className="text-lg font-semibold">Mot de passe modifié !</h2>
              <p className="text-sm text-muted-foreground">
                Redirection vers la connexion…
              </p>
            </div>
          ) : !sessionReady ? (
            <div className="text-center py-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                Vérification du lien en cours…
              </p>
              <p className="text-xs text-muted-foreground/60">
                Si rien ne se passe, le lien a peut-être expiré.{" "}
                <button onClick={() => navigate("/auth")} className="text-primary underline">
                  Demander un nouveau lien
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Nouveau mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Confirmer le mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full gap-2" size="lg" disabled={submitting}>
                {submitting ? "Chargement..." : "Modifier mon mot de passe"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
